/**
 * Wizard 2 Storage
 * Same logic as wizard-1 but uses wizard-4 keys and __wizardCurrentCountry
 */

class PetFlowStorage {
  constructor(flowName = 'wizard-4') {
    this.flowName = flowName;
    this.localStorageKey = `${flowName}-pet-data`;
    this.localStorageKeyPets = `${flowName}-pets-list`;
  }

  saveToLocalStorage(state) {
    try {
      const currentCountry = (typeof window !== 'undefined' && window.__wizardCurrentCountry) || '';
      const stateToSave = { ...state, _savedCountry: currentCountry };
      localStorage.setItem(this.localStorageKey, JSON.stringify(stateToSave));
      try {
        if (window.WizardPlan && window.WizardPlan.buyboxSnapshot && window.wizardFlow && window.Buybox2Journey) {
          window.Buybox2Journey.writePlan(window.WizardPlan.buyboxSnapshot(window.wizardFlow));
        }
      } catch (planError) {}
      if (stateToSave.petData?.name) {
        const shouldAdd = stateToSave.petMetaobjectId ||
          (stateToSave.currentStep && ['email', 'calculation', 'plan_overview', 'plan_onetime', 'profile'].includes(stateToSave.currentStep));
        if (shouldAdd) this.addPetNameToList(state.petData.name);
      }
      return true;
    } catch (e) {
      console.error('Error saving to localStorage:', e);
      return false;
    }
  }

  loadFromLocalStorage() {
    try {
      const s = localStorage.getItem(this.localStorageKey);
      return s ? JSON.parse(s) : null;
    } catch (e) {
      return null;
    }
  }

  clearLocalStorage() {
    localStorage.removeItem(this.localStorageKey);
    localStorage.removeItem(this.localStorageKeyPets);
    return true;
  }

  addPetNameToList(petName) {
    try {
      const list = this.getPetNamesList();
      const key = typeof petName === 'string' ? petName.trim().toLowerCase() : '';
      if (!key || list.includes(key)) return;
      list.push(key);
      localStorage.setItem(this.localStorageKeyPets, JSON.stringify(list));
    } catch (e) {}
  }

  getPetNamesList() {
    try {
      const s = localStorage.getItem(this.localStorageKeyPets);
      return s ? JSON.parse(s) : [];
    } catch (e) {
      return [];
    }
  }

  async petNameExists(petName, options = {}) {
    if (!petName) return null;

    const normalizedName = petName.trim().toLowerCase();
    const flow = options.flow || window.wizardFlow;
    const currentState = flow?.state;
    const currentMetaobjectId = currentState?.petMetaobjectId;
    const customerIdToCheck = options.customerId || currentState?.customerId;

    if (customerIdToCheck && flow?.shopifyAPI) {
      try {
        const shopifyPets = await flow.shopifyAPI.getAllPetMetaobjects(customerIdToCheck);
        for (const pet of shopifyPets) {
          const existing = (
            pet.petName ||
            pet.fields?.find(f => f.key === 'pet_name')?.value ||
            ''
          ).trim();
          if (!existing) continue;
          if (existing.toLowerCase() !== normalizedName) continue;
          if (currentMetaobjectId && pet.id === currentMetaobjectId) continue;

          let step = 'plan_overview';
          if (pet.fields) {
            const hasCalculations = pet.fields.some(f => f.key === 'dailyfoodg' && f.value);
            step = hasCalculations ? 'plan_overview' : 'email';
          }

          return {
            name: existing,
            step,
            source: 'shopify',
            metaobjectId: pet.id
          };
        }
      } catch (e) {
        console.error('Wizard-2: error checking Shopify pets for duplicate name:', e);
      }
    }

    // Local duplicate checks (align with wizard-1-storage petNameExists)
    const savedState = this.loadFromLocalStorage();
    const currentPetName = currentState?.petData?.name;

    if (savedState && savedState.petData && savedState.petData.name) {
      const savedName = savedState.petData.name.trim().toLowerCase();
      if (savedName === normalizedName) {
        const isCurrentPetNew = !currentMetaobjectId;
        const savedHasMetaobject = !!savedState.petMetaobjectId;
        const isDifferentPet = savedState.petMetaobjectId !== currentMetaobjectId;
        const isNewSessionWithOldName = isCurrentPetNew && savedHasMetaobject;

        if (isDifferentPet || isNewSessionWithOldName) {
          return {
            name: savedState.petData.name.trim(),
            step: savedState.currentStep || 'name',
            source: 'localStorage'
          };
        }
      }
    }

    const petsList = this.getPetNamesList();
    if (petsList.includes(normalizedName)) {
      const isDifferentName = currentPetName && currentPetName.trim().toLowerCase() !== normalizedName;
      const isNewPetCreation =
        !currentMetaobjectId &&
        (!currentPetName || currentPetName.trim().toLowerCase() !== normalizedName);

      if (isDifferentName || (isNewPetCreation && savedState && savedState.petMetaobjectId)) {
        const savedMatchesTyped =
          savedState &&
          savedState.petData &&
          savedState.petData.name &&
          savedState.petData.name.trim().toLowerCase() === normalizedName;

        if (savedMatchesTyped) {
          if (savedState.petMetaobjectId !== currentMetaobjectId) {
            return {
              name: savedState.petData.name.trim(),
              step: savedState.currentStep || 'name',
              source: 'localStorage'
            };
          }
        } else {
          // Name is on the pets list from a prior session; main pet-data key may describe another dog
          return {
            name: petName.trim(),
            step: 'name',
            source: 'localStorage'
          };
        }
      }
    }

    return null;
  }

  /**
   * Get customerId for a given email from localStorage (same behavior as wizard-1-storage).
   * Required by StepHandler.checkEmailMatch for the email step.
   */
  getCustomerIdForEmail(email) {
    if (!email) return null;

    const normalizedEmail = email.toLowerCase().trim();
    const savedState = this.loadFromLocalStorage();

    if (savedState && savedState.petData && savedState.petData.ownerEmail) {
      if (savedState.petData.ownerEmail.toLowerCase() === normalizedEmail) {
        if (savedState.sessionCustomerId) {
          return savedState.sessionCustomerId;
        }
        if (savedState.customerId) {
          return savedState.customerId;
        }
      }
    }

    return null;
  }

  /**
   * Check if email exists in saved wizard-4 localStorage state.
   * Required by StepHandler.checkEmailMatch for the email step.
   */
  emailExistsInStorage(email) {
    if (!email) return false;

    const normalizedEmail = email.toLowerCase().trim();
    const savedState = this.loadFromLocalStorage();

    if (savedState && savedState.petData && savedState.petData.ownerEmail) {
      return savedState.petData.ownerEmail.toLowerCase() === normalizedEmail;
    }

    return false;
  }

  async autoSave(state, customerId, options = {}) {
    this.saveToLocalStorage(state);
    if (options.forceShopifySave && customerId && window.wizardFlow?.shopifyAPI) {
      try {
        await window.wizardFlow.shopifyAPI.createOrUpdatePetMetaobject({
          petData: state.petData,
          calculations: state.calculations,
          customerId,
          petMetaobjectId: state.petMetaobjectId,
          resumeStep: options.nextStep,
          selectedFlavors: state.selectedFlavorsFull?.length ? state.selectedFlavorsFull : state.selectedFlavorsTrial,
          selectedFlavorsTrial: state.selectedFlavorsTrial,
          cartItems: state.cartItems
        });
      } catch (e) {
        console.warn('Wizard 2 Shopify auto-save failed:', e.message);
      }
    }
  }
}

window.PetFlowStorage = PetFlowStorage;
